import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Users, RefreshCw, AlertTriangle, Send, Sparkles, Star } from "lucide-react";
import { CtaButton, MonoLabel, SecondaryButton } from "../components/kit";
import api from "../lib/api";

const AVATAR_STYLES = [
  { from: "#F1795A", to: "#D24E2A", accessory: null },
  { from: "#3D86FF", to: "#0A55D6", accessory: null },
  { from: "#E8A93C", to: "#C07E1E", accessory: "glasses" },
  { from: "#35A17A", to: "#1F7D57", accessory: null },
  { from: "#3A3646", to: "#181621", accessory: "headphones" },
];

const Avatar3D = ({ idx, size = 40 }) => {
  const s = AVATAR_STYLES[idx % 5];
  const uid = `av${idx % 5}`;
  return (
    <svg width={size} height={size} viewBox="0 0 72 72" className="shrink-0" aria-hidden="true">
      <defs>
        <linearGradient id={`${uid}-bg`} x1="0" y1="0" x2="0.6" y2="1">
          <stop offset="0" stopColor={s.from} />
          <stop offset="1" stopColor={s.to} />
        </linearGradient>
        <linearGradient id={`${uid}-fig`} x1="0" y1="0" x2="0.6" y2="1">
          <stop offset="0" stopColor="#FFFFFF" />
          <stop offset="1" stopColor="#E4E4EC" />
        </linearGradient>
        <radialGradient id={`${uid}-gloss`} cx="0.3" cy="0.2" r="0.72">
          <stop offset="0" stopColor="#FFFFFF" stopOpacity="0.55" />
          <stop offset="1" stopColor="#FFFFFF" stopOpacity="0" />
        </radialGradient>
        <clipPath id={`${uid}-clip`}><circle cx="36" cy="36" r="35" /></clipPath>
      </defs>
      <g clipPath={`url(#${uid}-clip)`}>
        <rect width="72" height="72" fill={`url(#${uid}-bg)`} />
        <g style={{ filter: "drop-shadow(0 3px 4px rgba(20,15,10,.2))" }}>
          <circle cx="36" cy="29" r="13.5" fill={`url(#${uid}-fig)`} />
          <path d="M13 64c0-13 10-21 23-21s23 8 23 21z" fill={`url(#${uid}-fig)`} />
        </g>
        {s.accessory === "glasses" && (
          <g stroke="#2a2018" strokeOpacity="0.5" strokeWidth="2" fill="none">
            <circle cx="30" cy="29" r="4.6" />
            <circle cx="42" cy="29" r="4.6" />
            <path d="M34.6 29h2.8" />
          </g>
        )}
        {s.accessory === "headphones" && (
          <g>
            <path d="M22 28a14 14 0 0 1 28 0" stroke="#181621" strokeOpacity="0.55" strokeWidth="3" fill="none" />
            <rect x="18.5" y="26" width="6" height="11" rx="3" fill="#181621" fillOpacity="0.55" />
            <rect x="47.5" y="26" width="6" height="11" rx="3" fill="#181621" fillOpacity="0.55" />
          </g>
        )}
        <rect width="72" height="72" fill={`url(#${uid}-gloss)`} />
      </g>
    </svg>
  );
};

const PersonaCard = ({ p, idx }) => (
  <div className="card-soft p-5" data-testid={`persona-card-${idx}`}>
    <div className="flex items-start justify-between gap-2">
      <div className="flex items-center gap-3 min-w-0">
        <Avatar3D idx={idx} size={58} />
        <div className="min-w-0">
          <p className="font-bold text-[14px] text-[#181621] truncate">{p.name}, {p.age}</p>
          <p className="text-[11.5px] text-[#9A9591] truncate">{p.city} · {p.occupation}</p>
        </div>
      </div>
      {p.in_target ? (
        <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em] shrink-0" style={{ color: "#3B7A57", background: "rgba(59,122,87,.1)" }} data-testid={`persona-target-tag-${idx}`}>In target</span>
      ) : (
        <span className="inline-flex items-center rounded-full border border-[#E7E2D6] bg-white px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em] text-[#9A9591] shrink-0" data-testid={`persona-target-tag-${idx}`}>Control</span>
      )}
    </div>
    <p className="text-[12.5px] text-[#54505B] mt-3 leading-snug">{p.context}</p>
    <div className="mt-3 rounded-r-lg border-l-[3px] border-l-[#E0603A] bg-[#FAF8F3] px-3 py-2.5">
      <p className="text-[9.5px] font-bold tracking-[0.1em] uppercase text-[#E0603A]">Doubts</p>
      <p className="text-[11.5px] text-[#54505B] mt-0.5 leading-snug">{p.skepticism_bias}</p>
    </div>
  </div>
);

const ReplyCard = ({ p, text, idx }) => (
  <div
    className="rounded-2xl bg-white/85 backdrop-blur-md shadow-[0_8px_28px_rgba(24,22,33,0.10)] border border-white p-4 fade-up"
    style={{ animationDelay: `${idx * 0.18}s` }}
    data-testid={`panel-reply-${idx}`}
  >
    <div className="flex items-center gap-2.5">
      <Avatar3D idx={idx} size={36} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <p className="font-bold text-[13px] text-[#181621] truncate">
            {p.name}
            {!p.in_target && <span className="text-[9px] font-bold tracking-[0.1em] text-[#9A9591] ml-2 uppercase">Control</span>}
          </p>
          <span className="text-[10.5px] text-[#9A9591] shrink-0">now</span>
        </div>
        <p className="text-[11px] text-[#9A9591] truncate">{p.occupation}, {p.city}</p>
      </div>
    </div>
    <p className="text-[13.5px] text-[#181621] mt-2.5 leading-snug">{text}</p>
  </div>
);

const SYNTH_COLORS = { green: "#3E7B3E", amber: "#96691E", rose: "#B4574A" };

const Synthesis = ({ s }) => (
  <div className="card-soft p-6 mt-6" data-testid="panel-synthesis">
    <MonoLabel><Sparkles size={11} strokeWidth={2} /> SYNTHESIS</MonoLabel>
    <div className="space-y-3.5 mt-4">
      {[["What resonated", s.resonated, "green"], ["What confused", s.confused, "amber"], ["One fix", s.fix, "rose"]].map(([label, text, color]) => (
        <div key={label} className="flex gap-3 items-start">
          <span className="inline-flex items-center rounded-full border bg-white px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.06em] shrink-0 mt-0.5" style={{ color: SYNTH_COLORS[color], borderColor: `${SYNTH_COLORS[color]}40` }}>{label}</span>
          <p className="text-[13.5px] text-[#181621] leading-snug">{text}</p>
        </div>
      ))}
    </div>
  </div>
);

const EXAMPLE_CHIPS = ["an offer", "a price", "an ad hook", "a product name"];

export default function Panel() {
  const [panel, setPanel] = useState(undefined);
  const [session, setSession] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle | creating | testing | error
  const [input, setInput] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const pollRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    if (location.state?.prefill) setInput(location.state.prefill);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
  };

  const startPolling = () => {
    stopPolling();
    let ticks = 0;
    pollRef.current = setInterval(async () => {
      ticks += 1;
      if (ticks > 120) {
        stopPolling();
        setPhase("error");
        setErrMsg("The audience test took too long. Try again.");
        return;
      }
      try {
        const { data } = await api.get("/panel/status");
        if (data.status === "failed") {
          stopPolling();
          setPhase("error");
          setErrMsg(data.error || "The panel didn't answer. Try again.");
        } else if (data.status === "complete") {
          stopPolling();
          const res = await api.get("/panel/latest");
          setSession(res.data);
          setPhase("idle");
        }
      } catch { /* keep polling */ }
    }, 3000);
  };

  useEffect(() => {
    (async () => {
      try {
        const [{ data: p }, { data: job }, { data: latest }] = await Promise.all([
          api.get("/panel"),
          api.get("/panel/status"),
          api.get("/panel/latest"),
        ]);
        setPanel(p);
        setSession(latest);
        if (job.status === "pending") {
          setPhase("testing");
          startPolling();
        }
      } catch {
        setPanel(null);
      }
    })();
    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createPanel = async () => {
    setPhase("creating");
    try {
      const { data } = await api.post("/panel/generate");
      setPanel(data);
      setSession(null);
      setPhase("idle");
      toast("Your panel is assembled.", { description: "Five people, five different reasons to doubt you. Perfect." });
    } catch (err) {
      setPhase("idle");
      toast.error(err.response?.data?.detail || "Couldn't assemble your audience. Try again.");
    }
  };

  const runTest = async () => {
    if (!input.trim()) return;
    setPhase("testing");
    setErrMsg("");
    try {
      await api.post("/panel/test", { input: input.trim() });
      startPolling();
    } catch (err) {
      setPhase("idle");
      toast.error(err.response?.data?.detail || "Couldn't reach your audience. Try again.");
    }
  };

  const personaById = (id) => panel?.personas.find((p) => p.id === id);

  return (
    <div className="p-6 sm:p-10 max-w-4xl" data-testid="panel-page">
      <MonoLabel>Brainstorming Area · Audience Test</MonoLabel>
      <h1 className="mt-5 text-3xl sm:text-4xl leading-tight font-extrabold tracking-[-0.02em]">
        Five strangers, zero mercy
      </h1>
      <p className="mt-4 flex items-center gap-1.5 text-[12.5px] text-[#9A9591]" data-testid="panel-disclaimer">
        <Star size={13} strokeWidth={1.8} /> Directional feedback — not market research.
      </p>

      {panel === undefined && <div className="card-soft mt-8 p-10 animate-pulse h-40" data-testid="panel-loading" />}

      {panel === null && phase !== "creating" && (
        <div className="card-soft mt-8 p-10 sm:p-14 flex flex-col items-center text-center" data-testid="panel-empty-state">
          <div className="icon-tile !w-14 !h-14" style={{ background: "var(--neutral-bg)" }}>
            <Users size={26} strokeWidth={1.8} style={{ color: "var(--neutral-fg)" }} />
          </div>
          <p className="mt-5 text-lg font-bold text-[#181621]">No audience yet.</p>
          <p className="text-[14px] text-[#54505B] mt-1 max-w-md">I'll cast 5 personas from your Brand Brain — 3-4 from your real audience plus 1-2 outsiders as control. They stay the same across sessions.</p>
          <CtaButton className="mt-7" onClick={createPanel} data-testid="panel-create-btn">
            Assemble my audience
          </CtaButton>
        </div>
      )}

      {phase === "creating" && (
        <div className="card-soft mt-8 p-10 sm:p-14 text-center" data-testid="panel-creating-state">
          <span className="w-2 h-2 rounded-full bg-[#E0603A] inline-block animate-pulse" />
          <p className="mt-4 text-lg font-bold text-[#181621]">Casting your audience…</p>
          <p className="text-[13.5px] text-[#54505B] mt-1">Recruiting people who owe you nothing. A few seconds.</p>
        </div>
      )}

      {panel && phase !== "creating" && (
        <>
          <div className="flex items-center justify-between gap-3 mt-9 flex-wrap">
            <p className="text-[11px] font-bold tracking-[0.08em] text-[#9A9591] uppercase tabular-nums">Your audience — {panel.personas.length} people</p>
            <SecondaryButton className="!py-1.5 !px-3 text-[12px]" disabled={phase !== "idle"} onClick={createPanel} data-testid="panel-regenerate-btn">
              <RefreshCw size={12} strokeWidth={1.8} /> Recast audience
            </SecondaryButton>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4" data-testid="persona-grid">
            {panel.personas.map((p, i) => <PersonaCard key={p.id} p={p} idx={i} />)}
          </div>

          <div className="card-soft mt-8 p-6 sm:p-8" data-testid="panel-test-form">
            <p className="text-[11px] font-bold tracking-[0.08em] text-[#9A9591] uppercase">Test something</p>
            <div className="flex flex-wrap items-center gap-2 mt-2.5">
              {EXAMPLE_CHIPS.map((c) => (
                <span key={c} className="inline-flex items-center rounded-lg border border-[#E7E2D6] px-2.5 py-1 text-[11.5px] font-semibold text-[#9A9591] hover:text-[#181621] transition-colors cursor-default">{c}</span>
              ))}
              <span className="text-[12px] text-[#9A9591]">— as your audience would meet it cold.</span>
            </div>
            <textarea
              rows={3}
              className="field-input resize-none mt-4"
              placeholder='e.g. "Free grant check-up for manufacturers — you pay only if we win the grant"'
              value={input}
              onChange={(e) => setInput(e.target.value)}
              maxLength={600}
              data-testid="panel-input"
            />
            <div className="flex justify-end mt-4">
              <CtaButton onClick={runTest} disabled={phase !== "idle" || !input.trim()} data-testid="panel-submit-btn">
                {phase === "testing" ? "Your audience is reacting…" : <><Send size={14} strokeWidth={1.8} /> Ask your audience</>}
              </CtaButton>
            </div>
          </div>

          {phase === "testing" && (
            <div className="mt-6 space-y-3" data-testid="panel-testing-state">
              {panel.personas.map((p, i) => (
                <div key={p.id} className="rounded-2xl bg-white/70 border border-white p-4 flex items-center gap-3 animate-pulse" style={{ animationDelay: `${i * 0.12}s` }}>
                  <Avatar3D idx={i} size={36} />
                  <div className="flex-1">
                    <div className="h-2.5 w-28 rounded bg-[#F3EFE7]" />
                    <div className="h-2.5 w-3/4 rounded bg-[#F3EFE7] mt-2" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {phase === "error" && (
            <div className="card-soft mt-6 p-8 flex flex-col items-center text-center" data-testid="panel-error-state">
              <div className="icon-tile !w-12 !h-12" style={{ background: "var(--amber-bg)" }}>
                <AlertTriangle size={22} strokeWidth={1.8} style={{ color: "var(--amber-fg)" }} />
              </div>
              <p className="mt-4 font-bold text-[15px] text-[#181621]">{errMsg}</p>
              <CtaButton className="mt-5 !py-2.5 !px-5 text-[13.5px]" onClick={runTest} disabled={!input.trim()} data-testid="panel-retry-btn">
                <RefreshCw size={14} strokeWidth={1.8} /> Try again
              </CtaButton>
            </div>
          )}

          {phase === "idle" && session && (
            <div className="mt-8" data-testid="panel-session">
              <MonoLabel>AUDIENCE TEST ON: “{session.input.length > 60 ? session.input.slice(0, 60) + "…" : session.input}”</MonoLabel>
              <div className="mt-4 space-y-3" data-testid="panel-replies">
                {session.replies.map((r, i) => {
                  const p = personaById(r.persona_id);
                  return p ? <ReplyCard key={r.persona_id} p={p} text={r.text} idx={i} /> : null;
                })}
              </div>
              <Synthesis s={session.synthesis} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
